import React, { useState, useEffect, useRef } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { AuditLogQuery, AuditAction } from '../../types/audit';
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Field,
  Input,
  SearchInput,
  SegmentedControl,
  SegmentedItem,
  Select,
} from '../ui';
import { ENTITY_TYPES } from './auditFormat';

interface AuditLogFiltersProps {
  query: AuditLogQuery;
  onQueryChange: (query: AuditLogQuery) => void;
  className?: string;
}

const ACTION_OPTIONS: Array<{ value: AuditAction; label: string }> = [
  { value: AuditAction.CREATE, label: 'Create' },
  { value: AuditAction.READ, label: 'Read' },
  { value: AuditAction.UPDATE, label: 'Update' },
  { value: AuditAction.DELETE, label: 'Delete' },
  { value: AuditAction.LOGIN, label: 'Login' },
  { value: AuditAction.LOGOUT, label: 'Logout' },
  { value: AuditAction.APPROVE, label: 'Approve' },
  { value: AuditAction.REJECT, label: 'Reject' },
  { value: AuditAction.SEARCH, label: 'Search' },
];

/** Actions shown directly in the toolbar; the rest are in the filter sheet */
const QUICK_ACTIONS: AuditAction[] = [
  AuditAction.CREATE,
  AuditAction.UPDATE,
  AuditAction.DELETE,
  AuditAction.APPROVE,
  AuditAction.REJECT,
];

type RangePreset = 'any' | '1' | '7' | '30' | '90' | 'custom';

const RANGE_OPTIONS: Array<{ value: RangePreset; label: string }> = [
  { value: 'any', label: 'Any time' },
  { value: '1', label: '24 hours' },
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
];

const DEBOUNCE_MS = 400;

export const AuditLogFilters: React.FC<AuditLogFiltersProps> = ({
  query,
  onQueryChange,
  className = '',
}) => {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [localQuery, setLocalQuery] = useState<AuditLogQuery>(query);
  const [entityIdDraft, setEntityIdDraft] = useState(query.entityId ?? '');
  // Preset the current startDate came from, so the select can show it
  const presetRef = useRef<{ preset: RangePreset; startDate?: string }>({ preset: 'any' });
  const queryRef = useRef(query);
  queryRef.current = query;

  // Update local query when prop changes
  useEffect(() => {
    setLocalQuery(query);
    setEntityIdDraft(query.entityId ?? '');
  }, [query]);

  // Search pill applies after a short pause
  useEffect(() => {
    const current = queryRef.current;
    if ((current.entityId ?? '') === entityIdDraft.trim()) return;
    const timer = window.setTimeout(() => {
      if ((queryRef.current.entityId ?? '') === entityIdDraft.trim()) return;
      onQueryChange({ ...queryRef.current, entityId: entityIdDraft.trim() || undefined, page: 1 });
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityIdDraft]);

  const handleInputChange = (field: keyof AuditLogQuery, value: string | number | undefined) => {
    const updatedQuery = { ...localQuery, [field]: value || undefined };
    setLocalQuery(updatedQuery);
  };

  const apply = (changes: Partial<AuditLogQuery>) => {
    onQueryChange({ ...query, ...changes, page: 1 });
  };

  const handleApplyFilters = () => {
    presetRef.current = { preset: 'custom', startDate: localQuery.startDate };
    onQueryChange({ ...localQuery, page: 1 });
    setSheetOpen(false);
  };

  const handleClearFilters = () => {
    const clearedQuery: AuditLogQuery = { page: 1, limit: 50 };
    presetRef.current = { preset: 'any' };
    setLocalQuery(clearedQuery);
    setEntityIdDraft('');
    onQueryChange(clearedQuery);
    setSheetOpen(false);
  };

  const formatDateForInput = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toISOString().split('T')[0];
  };

  const handleDateChange = (field: 'startDate' | 'endDate', value: string) => {
    const dateValue = value ? new Date(value).toISOString() : undefined;
    handleInputChange(field, dateValue);
  };

  const rangeValue: RangePreset = (() => {
    if (!query.startDate && !query.endDate) return 'any';
    const remembered = presetRef.current;
    if (!query.endDate && remembered.preset !== 'custom' && remembered.startDate === query.startDate) {
      return remembered.preset;
    }
    return 'custom';
  })();

  const handleRangeChange = (value: RangePreset) => {
    if (value === 'custom') {
      openSheet();
      return;
    }
    if (value === 'any') {
      presetRef.current = { preset: 'any' };
      apply({ startDate: undefined, endDate: undefined });
      return;
    }
    const start = new Date(Date.now() - Number(value) * 24 * 60 * 60 * 1000).toISOString();
    presetRef.current = { preset: value, startDate: start };
    apply({ startDate: start, endDate: undefined });
  };

  const openSheet = () => {
    setLocalQuery(query);
    setSheetOpen(true);
  };

  type ActionValue = 'all' | AuditAction;
  const actionItems: SegmentedItem<ActionValue>[] = [
    { value: 'all', label: 'All' },
    ...ACTION_OPTIONS.filter(o => QUICK_ACTIONS.includes(o.value) || o.value === query.action).map(o => ({
      value: o.value,
      label: o.label,
    })),
  ];

  // Badge counts what the toolbar hides at each width
  const phoneCount = [query.action, query.entityType, query.userId, query.startDate || query.endDate].filter(Boolean).length;
  const tabletCount = [query.action, query.entityType, query.userId].filter(Boolean).length;
  const desktopCount = [query.entityType, query.userId].filter(Boolean).length;

  const countBadge = (count: number, extra = '') =>
    count > 0 ? (
      <span
        className={`inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary-600 px-1 text-[10px] font-medium tabular-nums text-white ${extra}`}
      >
        {count}
      </span>
    ) : null;

  return (
    <div className={className}>
      {/* Toolbar */}
      <div className="flex items-center gap-2">
        <SearchInput
          aria-label="Entity ID"
          placeholder="Entity ID"
          className="min-w-0 flex-1 sm:w-56 sm:flex-none"
          value={entityIdDraft}
          onChange={e => setEntityIdDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') apply({ entityId: entityIdDraft.trim() || undefined });
          }}
          spellCheck={false}
          autoComplete="off"
        />
        <SegmentedControl<ActionValue>
          label="Action"
          className="hidden min-w-0 md:inline-flex"
          items={actionItems}
          value={query.action ?? 'all'}
          onChange={value => apply({ action: value === 'all' ? undefined : value })}
        />
        <Select
          aria-label="Date range"
          className="hidden w-32 flex-shrink-0 sm:block"
          value={rangeValue}
          onChange={e => handleRangeChange(e.target.value as RangePreset)}
        >
          {RANGE_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
          <option value="custom">Custom…</option>
        </Select>
        <Button
          variant="secondary"
          onClick={openSheet}
          aria-label="Filters"
          className="h-10 flex-shrink-0 sm:ml-auto sm:h-9"
        >
          <SlidersHorizontal className="h-4 w-4 text-neutral-500" strokeWidth={1.75} />
          <span className="hidden sm:inline">Filters</span>
          {countBadge(phoneCount, 'sm:hidden')}
          {countBadge(tabletCount, 'hidden sm:inline-flex md:hidden')}
          {countBadge(desktopCount, 'hidden md:inline-flex')}
        </Button>
      </div>

      {/* Filter sheet */}
      <Dialog
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        labelledBy="audit-filters-title"
        handle
        className="max-h-[92vh] sm:max-w-md"
      >
        <DialogHeader id="audit-filters-title" title="Filters" onClose={() => setSheetOpen(false)} />
        <DialogBody className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Action" htmlFor="action">
              <Select
                id="action"
                value={localQuery.action || ''}
                onChange={e => handleInputChange('action', e.target.value as AuditAction)}
              >
                <option value="">All</option>
                {ACTION_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Entity type" htmlFor="entityType">
              <Select
                id="entityType"
                value={localQuery.entityType || ''}
                onChange={e => handleInputChange('entityType', e.target.value)}
              >
                <option value="">All</option>
                {ENTITY_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="From" htmlFor="startDate">
              <Input
                type="date"
                id="startDate"
                value={formatDateForInput(localQuery.startDate)}
                onChange={e => handleDateChange('startDate', e.target.value)}
              />
            </Field>
            <Field label="To" htmlFor="endDate">
              <Input
                type="date"
                id="endDate"
                value={formatDateForInput(localQuery.endDate)}
                onChange={e => handleDateChange('endDate', e.target.value)}
              />
            </Field>
          </div>
          <Field label="Entity ID" htmlFor="entityId">
            <Input
              id="entityId"
              value={localQuery.entityId || ''}
              onChange={e => handleInputChange('entityId', e.target.value)}
              className="font-ui-mono"
              spellCheck={false}
              autoComplete="off"
            />
          </Field>
          <Field label="User ID" htmlFor="userId">
            <Input
              id="userId"
              value={localQuery.userId || ''}
              onChange={e => handleInputChange('userId', e.target.value)}
              className="font-ui-mono"
              spellCheck={false}
              autoComplete="off"
            />
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={handleClearFilters} className="mr-auto">
            Clear
          </Button>
          <Button variant="secondary" onClick={() => setSheetOpen(false)} className="hidden sm:inline-flex">
            Cancel
          </Button>
          <Button variant="primary" onClick={handleApplyFilters}>
            Apply
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
};
